import { Banner, Button, LegacyStack, Modal, Text, TextContainer } from '@shopify/polaris';
import DropdownButton from '../Common/DropdownButton';
import { filterPermissionsMenu } from '../../../services/permissionsService';
import { useContext, useEffect, useMemo, useState } from 'react';
import { PDFModal } from '../Modals/PDFModal';
import ToastContext from '../../../context/ToastContext';
import { SET_ERROR, SET_OK } from '../../../context/ToastReducer';
import { useHistory } from 'react-router';
import { useTransferMutations } from '../../../customHooks/useTransferQueries';

// Configuración por acción: copy del modal, etiqueta del dropdown, variante
// visual, banner de aviso y permiso requerido. Mismo patrón que el de
// inventario (ViewInventoryPrimaryActions) para que ambos detalles se sientan
// uniformes.
const ACTION_CONFIG = {
  TRANSIT: {
    label: 'Enviar a destino',
    title: 'Enviar mercancía a destino',
    description: (t) =>
      `Vas a poner la transferencia en tránsito de ${t.originDeposit?.name} hacia ${t.destinationDeposit?.name}. Una vez enviada no se podrá modificar la mercancía planificada.`,
    confirmText: 'Enviar a destino',
    destructive: false,
    permissions: ['STOCK:CLOSE'],
  },
  CLOSE: {
    label: 'Cerrar con lo recibido',
    title: 'Cerrar transferencia',
    description: () =>
      'Se cerrará la transferencia con la mercancía recibida hasta ahora. La que no se haya recibido no se traspasará al destino: se apuntará como extraviada del depósito origen.',
    warning: 'Esta acción es irreversible y modificará el stock del depósito destino.',
    confirmText: 'Cerrar transferencia',
    destructive: false,
    permissions: ['STOCK:CLOSE'],
  },
  ACCEPT_ALL: {
    label: 'Aceptar todo y cerrar',
    title: 'Aceptar todo y cerrar',
    description: () =>
      'Se aceptará como recibida toda la mercancía planificada (incluso la que aún no se haya anotado) y se cerrará la transferencia.',
    warning: 'Esta acción es irreversible.',
    confirmText: 'Aceptar todo y cerrar',
    destructive: false,
    permissions: ['STOCK:CLOSE'],
  },
  REJECT_ALL: {
    label: 'Rechazar todo y cerrar',
    title: 'Rechazar todo y cerrar',
    description: () =>
      'Se rechazará la mercancía no recibida y se cerrará la transferencia. Solo se traspasará al destino lo que ya esté anotado como recibido.',
    warning: 'Esta acción es irreversible.',
    confirmText: 'Rechazar todo y cerrar',
    destructive: true,
    permissions: ['STOCK:CLOSE'],
  },
  REOPEN: {
    label: 'Reabrir transferencia',
    title: 'Reabrir transferencia',
    description: () =>
      'La transferencia volverá al estado IN_TRANSIT y se eliminarán los apuntes de stock generados al cerrarla. Podrás volver a anotar recepciones y cerrar de nuevo.',
    warning: 'Esta acción no está disponible si la transferencia se gestionó vía Hopper.',
    confirmText: 'Reabrir',
    destructive: false,
    permissions: ['STOCK:CLOSE'],
  },
  DELETE: {
    label: 'Eliminar transferencia',
    title: 'Eliminar transferencia',
    description: (t) => {
      if (t.status === 'PENDING')
        return 'Se eliminará la transferencia por completo. Como aún no ha salido del origen, no se moverá stock vivo.';
      return `Se eliminará la transferencia y la mercancía que figuraba en tránsito volverá contablemente al depósito origen (${t.originDeposit?.name}).`;
    },
    warning: 'Esta acción es irreversible y eliminará todos los asientos contables asociados.',
    confirmText: 'Eliminar',
    destructive: true,
    permissions: ['STOCK:DELETE'],
  },
};

// Cerrar sin haber anotado nada es casi siempre un descuido, no una decisión:
// así se cerraron traspasos enteros que nadie recibió y cuya mercancía acabó
// desapareciendo del libro. El modal lo dice y ofrece la salida buena
// (aceptar todo) sin obligar a cancelar y volver a empezar.
const EMPTY_CLOSE_CONFIG = {
  title: 'No has anotado ninguna recepción',
  confirmText: 'Continuar',
  acceptAllText: 'Cerrar aceptando todo',
  destructive: false,
  warning: 'Si continúas, toda la mercancía enviada se dará por extraviada.',
};

// Acciones aplicables según el estado del transfer. Como el back no expone
// `transfer.actions` (a diferencia de inventarios), lo decide el cliente.
// Si en una sesión futura se añade ese campo al GET, este mapa se simplifica.
// Las transferencias gestionadas vía Hopper no se pueden borrar localmente
// porque dejarían Shopify inconsistente. Mismo criterio que REOPEN.
//
// Una transferencia cerrada no se puede borrar: el back lo rechaza porque el
// borrado arrasaría con el stock que ya entró en el destino. Para borrarla hay
// que reabrirla primero, así que en CLOSED solo se ofrece REOPEN.
const actionsByStatus = (transfer) => {
  if (!transfer) return [];
  const isHopper = Boolean(transfer.hopperPayload);
  switch (transfer.status) {
    case 'PENDING':
      return isHopper ? ['TRANSIT'] : ['TRANSIT', 'DELETE'];
    case 'IN_TRANSIT':
      return isHopper ? ['CLOSE', 'ACCEPT_ALL', 'REJECT_ALL'] : ['CLOSE', 'ACCEPT_ALL', 'REJECT_ALL', 'DELETE'];
    case 'CLOSED':
      return isHopper ? [] : ['REOPEN'];
    default:
      return [];
  }
};

const ViewTransferPrimaryActions = ({ transfer, returnUrl }) => {
  const [, dispatchToast] = useContext(ToastContext);
  const history = useHistory();
  const id = transfer?.id;
  const mutations = useTransferMutations(id);

  // Acción seleccionada y pendiente de confirmación. null = sin modal abierto.
  const [pendingAction, setPendingAction] = useState(null);

  const [activePDFModal, setActivePDFModal] = useState(false);
  const [queryPDF, setQueryPDF] = useState({ transferId: id });

  const handlePDFExport = () => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const q = {};
    for (const [key, value] of urlParams) q[key] = value;
    delete q['r'];
    setQueryPDF({ ...queryPDF, ...q });
    setActivePDFModal(true);
  };

  const mutationFor = (action) => {
    switch (action) {
      case 'TRANSIT':
        return mutations.transit;
      case 'CLOSE':
        return mutations.close;
      case 'ACCEPT_ALL':
        return mutations.acceptAll;
      case 'REJECT_ALL':
        return mutations.rejectAll;
      case 'REOPEN':
        return mutations.reopen;
      case 'DELETE':
        return mutations.deleteOne;
      default:
        return null;
    }
  };

  const runAction = (action) => {
    const mutation = mutationFor(action);
    if (!mutation) return;

    mutation.mutate(undefined, {
      onSuccess: (r) => {
        dispatchToast({ type: SET_OK, payload: r?.data });
        setPendingAction(null);
        if (action === 'DELETE' && returnUrl) history.push(returnUrl);
      },
      onError: (e) => {
        dispatchToast({ type: SET_ERROR, payload: e.response?.data || { message: 'Error al ejecutar la acción' } });
        setPendingAction(null);
      },
    });
  };

  const handleConfirm = () => {
    if (!pendingAction) return;
    runAction(pendingAction);
  };

  // Se cierra sin nada anotado: hay mercancía enviada y el recibido es 0.
  const isEmptyClose =
    pendingAction === 'CLOSE' && Number(transfer?.stockQty) > 0 && Number(transfer?.stockReceivedQty) === 0;

  const isLoading = pendingAction
    ? Boolean(mutationFor(pendingAction)?.isLoading) || (isEmptyClose && Boolean(mutations.acceptAll?.isLoading))
    : false;

  const handleCloseModal = () => {
    if (isLoading) return;
    setPendingAction(null);
  };

  const dropdownItems = useMemo(() => {
    const available = actionsByStatus(transfer);
    return available
      .filter((a) => ACTION_CONFIG[a])
      .map((a) => ({
        content: ACTION_CONFIG[a].label,
        onAction: () => setPendingAction(a),
        destructive: ACTION_CONFIG[a].destructive,
        permissions: ACTION_CONFIG[a].permissions,
      }));
  }, [transfer]);

  const dropdownItemsExport = [
    { content: 'Exportar a PDF', disabled: false, onAction: handlePDFExport },
    { content: 'Exportar a CSV', disabled: true },
  ];

  if (!transfer) return null;

  const config = pendingAction ? ACTION_CONFIG[pendingAction] : null;
  const emptyCloseDescription = isEmptyClose
    ? `No se ha anotado ninguna unidad de las ${transfer.stockQty} enviadas a ${transfer.destinationDeposit?.name}. ` +
      'Si la mercancía ha llegado y solo falta anotarla, cierra aceptando todo. Si de verdad no ha llegado nada, ' +
      'continúa: las unidades se apuntarán como extraviadas del depósito origen.'
    : null;

  return (
    <LegacyStack>
      <DropdownButton
        title={'Exportar'}
        items={dropdownItemsExport}
        buttonProps={{ disabled: dropdownItemsExport.every((i) => i.disabled) }}
      />
      {dropdownItems.length > 0 && (
        <DropdownButton title={'Acciones'} items={filterPermissionsMenu(dropdownItems)} buttonProps={{}} />
      )}

      {config && (
        <Modal
          open={Boolean(pendingAction)}
          onClose={handleCloseModal}
          title={isEmptyClose ? EMPTY_CLOSE_CONFIG.title : config.title}
          primaryAction={{
            content: isEmptyClose ? EMPTY_CLOSE_CONFIG.confirmText : config.confirmText,
            destructive: isEmptyClose ? EMPTY_CLOSE_CONFIG.destructive : config.destructive,
            onAction: handleConfirm,
            loading: isLoading,
          }}
          secondaryActions={
            isEmptyClose
              ? [
                  {
                    content: EMPTY_CLOSE_CONFIG.acceptAllText,
                    onAction: () => runAction('ACCEPT_ALL'),
                    disabled: isLoading,
                  },
                  { content: 'Cancelar', onAction: handleCloseModal, disabled: isLoading },
                ]
              : [{ content: 'Cancelar', onAction: handleCloseModal, disabled: isLoading }]
          }
        >
          <Modal.Section>
            <TextContainer>
              <p>{isEmptyClose ? emptyCloseDescription : config.description(transfer)}</p>
              {(isEmptyClose ? EMPTY_CLOSE_CONFIG.warning : config.warning) && (
                <Banner
                  status={isEmptyClose ? 'warning' : config.destructive ? 'critical' : 'warning'}
                  title={isEmptyClose ? EMPTY_CLOSE_CONFIG.warning : config.warning}
                />
              )}
            </TextContainer>
          </Modal.Section>
        </Modal>
      )}

      <PDFModal
        title={'RECEPCIÓN DE MERCANCÍA'}
        subtitle={
          transfer ? `${transfer.name} \n\n ${transfer.originDeposit.name} -> ${transfer.destinationDeposit.name}` : null
        }
        active={activePDFModal}
        onClose={() => setActivePDFModal(false)}
        domain={'transfers'}
        model={'stock'}
        query={queryPDF}
        attributesDefault={{ cover: true, showNumbers: true, empty: false, blink: false }}
        AttributesWrapper={PDFAttributes}
      />
    </LegacyStack>
  );
};

const PDFAttributes = ({ attr, onAttrChange = () => {} }) => {
  const [attributes, setAttributes] = useState(attr);

  useEffect(() => {
    onAttrChange(attributes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes]);

  return (
    <LegacyStack spacing="loose">
      <LegacyStack.Item>
        <>
          <Text variant="bodyMd">Propiedades</Text>
          <Button primary={attributes.cover} onClick={() => setAttributes({ ...attributes, cover: !attributes.cover })}>
            Incluir portada
          </Button>
        </>
      </LegacyStack.Item>
    </LegacyStack>
  );
};

export default ViewTransferPrimaryActions;
